import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/context/I18nContext";
import { ChevronLeft, ChevronRight, Rocket, CheckCircle2 } from "lucide-react";

const ITEMS = [
  {
    id: "punch", type: "web", src: "/modules/punch.png", suffix: "PUNCH", accent: "#EAB308", status: "live",
    tag: { fr: "Gestion du temps & chantier", en: "Time & job-site management" },
  },
  {
    id: "erp", type: "web", src: "/modules/erp.png", suffix: "ERP", accent: "#dc143c", status: "soon",
    tag: { fr: "Projets, finances & ressources", en: "Projects, finance & resources" },
  },
  {
    id: "devis", type: "web", src: "/modules/devis.png", suffix: "DEVIS AI", accent: "#EAB308", status: "soon",
    tag: { fr: "Estimation intelligente par IA", en: "AI-powered estimating" },
  },
  {
    id: "mobile", type: "mobile", src: "/mobile/dashboard.png", suffix: "MOBILE", accent: "#EAB308", status: "soon",
    tag: { fr: "Tout votre chantier dans votre poche", en: "Your whole job site in your pocket" },
  },
  {
    id: "billing", type: "mobile", src: "/mobile/billing.png", suffix: "COMPTA", accent: "#dc143c", status: "soon",
    tag: { fr: "Facturation, taxes & abonnement", en: "Billing, taxes & subscription" },
  },
];

const AUTOPLAY = 3800;

export function AppShowcaseSection() {
  const { lang } = useI18n();
  const l = lang === "en" ? "en" : "fr";
  const [[idx, dir], setState] = useState([0, 1]);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const n = ITEMS.length;
  const item = ITEMS[idx];

  const paginate = (d) => setState(([i]) => [(i + d + n) % n, d]);
  const goTo = (i) => setState(([cur]) => [i, i > cur ? 1 : -1]);

  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(() => paginate(1), AUTOPLAY);
    return () => clearTimeout(timer.current);
  }, [idx, paused]); // eslint-disable-line

  const tt = {
    fr: { eyebrow: "L'écosystème en action", title: "Vos applications", titleB: "prennent vie", desc: "Des interfaces réelles, pensées pour la construction au Québec. Découvrez chaque module — et ce qui s'en vient.", soon: "Module à venir", live: "Disponible maintenant" },
    en: { eyebrow: "The ecosystem in action", title: "Your apps", titleB: "come to life", desc: "Real interfaces, built for construction in Québec. Discover every module — and what's coming next.", soon: "Coming soon", live: "Available now" },
  }[l];

  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 120 : -120, rotateY: d > 0 ? 18 : -18, scale: 0.9 }),
    center: { opacity: 1, x: 0, rotateY: 0, scale: 1 },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -120 : 120, rotateY: d > 0 ? -18 : 18, scale: 0.9 }),
  };

  const Device = ({ it }) => it.type === "mobile" ? (
    <div className="pg-phone pg-phone--front w-[230px] sm:w-[250px] mx-auto">
      <div className="pg-phone__notch" />
      <img src={it.src} alt={it.suffix} className="pg-phone__screen" />
    </div>
  ) : (
    <div className="pg-browser pg-browser--front w-full max-w-[640px] mx-auto">
      <div className="pg-browser__bar">
        <span className="pg-browser__dot" style={{ background: "#dc143c" }} />
        <span className="pg-browser__dot" style={{ background: "#EAB308" }} />
        <span className="pg-browser__dot" style={{ background: "#22c55e" }} />
        <span className="pg-browser__url">{it.id}.progere.ca</span>
      </div>
      <img src={it.src} alt={it.suffix} className="pg-browser__screen" />
    </div>
  );

  return (
    <section id="showcase" className="relative py-28 lg:py-36 overflow-hidden bg-[#050505]" data-testid="app-showcase-section">
      {/* Orbes animés */}
      <motion.div className="pointer-events-none absolute -top-20 -right-20 w-[560px] h-[560px] pg-radial-gold opacity-40"
        animate={{ scale: [1, 1.15, 1], opacity: [0.35, 0.5, 0.35] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="pointer-events-none absolute -bottom-24 -left-16 w-[520px] h-[520px] rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, rgba(220,20,60,0.5), transparent 70%)" }}
        animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }} />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        {/* En-tête */}
        <motion.div className="max-w-2xl" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.6 }}>
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#dc143c]">{tt.eyebrow}</span>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.02]">
            {tt.title} <span className="bg-gradient-to-r from-[#EAB308] to-[#dc143c] bg-clip-text text-transparent">{tt.titleB}</span>
          </h2>
          <p className="mt-4 text-white/60 text-lg">{tt.desc}</p>
        </motion.div>

        <div className="mt-12 grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-8 items-center">
          {/* Panneau infos (change avec le slide) */}
          <div className="min-w-0 order-2 lg:order-1">
            <AnimatePresence mode="wait">
              <motion.div key={item.id}
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.4 }}>
                <span className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-black uppercase tracking-wider ${item.status === "live" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-[#dc143c] text-white"}`} data-testid="app-showcase-status">
                  {item.status === "live" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Rocket className="w-3.5 h-3.5" />}
                  {item.status === "live" ? tt.live : tt.soon}
                </span>
                <h3 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight leading-none" data-testid="app-showcase-title">
                  <span className="pg-pro">PRO</span><span className="pg-gere">GÈRE</span>
                  <span className="block text-white mt-1">{item.suffix}</span>
                </h3>
                <p className="mt-4 text-white/60 text-lg max-w-sm">{item.tag[l]}</p>
              </motion.div>
            </AnimatePresence>

            {/* Contrôles */}
            <div className="mt-8 flex items-center gap-4">
              <button type="button" onClick={() => paginate(-1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white hover:border-[#EAB308]/50 transition-colors" aria-label="Précédent"><ChevronLeft className="w-5 h-5" /></button>
              <button type="button" onClick={() => paginate(1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white hover:border-[#EAB308]/50 transition-colors" aria-label="Suivant"><ChevronRight className="w-5 h-5" /></button>
              <div className="flex items-center gap-2 ml-2" data-testid="app-showcase-dots">
                {ITEMS.map((_, i) => (
                  <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Voir ${ITEMS[i].suffix}`} data-testid={`app-showcase-dot-${i}`}
                    className={`h-2 rounded-full transition-all ${i === idx ? "w-7 bg-[#EAB308]" : "w-2 bg-white/25 hover:bg-white/50"}`} />
                ))}
              </div>
            </div>
          </div>

          {/* Scène 3D swappante */}
          <div className="relative min-w-0 order-1 lg:order-2 h-[320px] sm:h-[420px] flex items-center justify-center" style={{ perspective: 1400 }}>
            <div className="pointer-events-none absolute inset-0 blur-3xl opacity-60"
              style={{ background: `radial-gradient(circle at 50% 50%, ${item.accent}22, transparent 65%)` }} />
            <AnimatePresence mode="popLayout" custom={dir}>
              <motion.div key={item.id} custom={dir} variants={variants}
                initial="enter" animate="center" exit="exit"
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 flex items-center justify-center"
                style={{ transformStyle: "preserve-3d" }}>
                <Device it={item} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
